import {DrawingType, Status} from "../../../models/enums";
import {useState} from "react";
import {apiInstance} from "../../../api/api";
import {UpdateDrawingApiDto} from "../../../models/restModels";
import {
	AreaMeasurement,
	CountMeasurement,
	DrawingItem,
	EditorDrawingData, LengthMeasurement, Measurement
} from "../../project-drawings/features/editor/models/editor";
import LoggerService from "../../../services/LoggerService";
import NotificationService from "../../../services/NotificationService";
import {useTranslation} from "react-i18next";
import {DrawingErrorTypes} from "../../project-drawings/features/editor/models/enums";
import {pages, useNavigator} from "../../navigator";
import SerializedMeasurement from "../../../models/SerializedMeasurement";

function useCreateAssetPlanFromAsBuilt(
	projectId: string,
	sectionId: string,
	costCenterId: string,
	drawingId: string,
	versionId: string,
	newDrawingName: string
) {
	const [saveStatus, setSaveStatus] = useState(Status.IDLE);
    const [assetPlanWithNoAssetsActive, setAssetPlanWithNoAssetsActive] = useState(false);
	const {navigateTo} = useNavigator()
	const {t} = useTranslation();

	const errorTranslations = {
		[DrawingErrorTypes.VERSION_LOCK]: t(`projects.details.createAssetBuilder.createAssetBuilderErrDescVersionLock`),
		[DrawingErrorTypes.DRAWING_LOCK]: t(`projects.details.createAssetBuilder.createAssetBuilderErrDescDrawingLock`),
		[DrawingErrorTypes.DEFAULT]: t(`projects.details.createAssetBuilder.createAssetBuilderErrDesc`)
	}

	function handleError(err: any) {
		setSaveStatus(Status.ERROR);
		LoggerService.logError(err);
		let errorKey: DrawingErrorTypes;
		if (err?.response?.data?.exception?.includes("SourceDrawingVersionIsLockedException")) {
			errorKey = DrawingErrorTypes.VERSION_LOCK;
		}
		else if (err?.response?.data?.exception?.includes("DrawingLockedException")) {
			errorKey = DrawingErrorTypes.DRAWING_LOCK;
		}
		else {
			errorKey = DrawingErrorTypes.DEFAULT;
		}
		NotificationService.errorNotification(t("common.error"), errorTranslations[errorKey])
	}

	function handleSuccess(newDrawingId: string, newVersionId: string) {
		setSaveStatus(Status.SUCCESS);
		NotificationService.successNotification(
			t("common.success"),
			t("projects.details.createAssetBuilder.createAssetBuilderSuccessDesc")
		)
		navigateTo(pages.projectDrawingPage(
			'jobs', projectId, sectionId, costCenterId, newDrawingId, newVersionId
		))
	}

	function isAssetMaterial(drawingItem: SerializedMeasurement): boolean {
		return !!(drawingItem && drawingItem.isAsset && drawingItem.assetTypeId !== null);
	}


	function isAssetData(drawingItem: DrawingItem): boolean {
		if (drawingItem.data && 'material' in drawingItem.data) {
			let materialData = drawingItem.data['material']
			return !!(materialData && materialData.isAsset && materialData.assetTypeId !== null);
		} else {
			return false;
		}
	}

	function returnAssetData(drawingItem: EditorDrawingData): EditorDrawingData{
		if (drawingItem.items) {
			drawingItem.items = drawingItem.items.filter(item => isAssetData(item));
		}
		return drawingItem;
	}

    function isAnyAssetPlaced(drawingItem: EditorDrawingData): boolean {
        return drawingItem.items.some((item) =>
			(item.data as Measurement).material.isAsset &&
			((item.data as CountMeasurement).countItems?.length > 0 ||
				(item.data as AreaMeasurement).areaFragments?.length > 0 ||
				(item.data as LengthMeasurement).lengthFragments?.length > 0
			)
		)
    }

    const createAssetPlan = () => {
        setSaveStatus(Status.LOADING);
        apiInstance.drawingsApi.fetchDrawingData(versionId)
            .then(drawingData => {
                let {data} = drawingData;
                if (!isAnyAssetPlaced((data) as EditorDrawingData)) {
                    setSaveStatus(Status.IDLE);
                    setAssetPlanWithNoAssetsActive(true);
                    return false;
                }
                return true;
            })
            .then(bool => {
                if (bool) {
                    apiInstance.drawingsApi.postDrawingFromSource(projectId, sectionId, costCenterId, newDrawingName, drawingId,
                        DrawingType.ASSET_PLAN)
                        .then((drawingDetails) => {
                            apiInstance.drawingsApi.fetchDrawingData(versionId)
                                .then(drawingData => {
                                    let {data, measurements} = drawingData;
                                    let newMeasurements: SerializedMeasurement[] = measurements.filter(item => isAssetMaterial(item));
                                    let newData = returnAssetData((data) as EditorDrawingData);
                                    const putDrawingData: UpdateDrawingApiDto = {
                                        data: (newData) as EditorDrawingData,
                                        measurements: newMeasurements,
                                        sourceVersionId: versionId
                                    };
                                    if (data) {
                                        apiInstance.drawingsApi.putDrawingData(drawingDetails.id, putDrawingData)
                                            .then((drawingVersion) => {
                                                handleSuccess(drawingDetails.id, drawingVersion.id);
                                            })
                                            .catch(handleError);
                                    }
                                })
                                .catch(handleError);
                        })
                        .catch(handleError);
                }
            });
    };

    const handlePromptClose = () => {
        setAssetPlanWithNoAssetsActive(false);
    };

    return {
        createAssetPlan,
        saveStatus,
        assetPlanWithNoAssetsActive,
        handlePromptClose
    };
}

export default useCreateAssetPlanFromAsBuilt;